import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useSessionFormMedia from "./useSessionFormMedia";
import SessionMediaSection from "./SessionMediaSection";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Box, TextField, FormControlLabel, Switch, Button, Typography, Paper, Chip, Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { fetchAllBGM } from "../../actions/backgroundMusicActions";
import { fetchAllChallenges } from "../../actions/challengeActions";
import { fetchAuthors } from "../../actions/authorsActions";
import { fetchAllCategories } from "../../actions/categoryActions";
import { clearMedia, uploadAudio, uploadImage } from "../../actions/mediaActions";
import { fetchAllSeries } from "../../actions/seriesActions";
import { addEditSession, deleteSession, fetchOneSession, clearSessions } from "../../actions/sessionActions";
import { fetchRecommendedList, fetchPopularList } from "../../actions/sessionListsActions";
import { fetchAllSubCategories } from "../../actions/subcategoryActions";
import { fetchTappingPoints } from "../../actions/tappingPointsActions";
import TappingPointsTimestamp from "./TappingPointsTimestamp/TappingPointsTimestamp";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import { selectTappingPoints, selectBackgroundMusics } from "./selectors";
import BackgroundMusicSelector from "../Common/BackgroundMusicSelector";

import AuthorSelector from "../Common/AuthorSelector";
import ParentSelector from "../Common/ParentSelector";
import TagsSection from "../Common/TagsSection";
import TimeInput from "../Common/TimeInput";
import { formatTimeToMMSS, parseMMSSToSeconds } from "../../utils/timeFormat";
import { preventEnterFormSubmission } from "../../utils/formUtils";

const AddEditSession = ({ isEdit, selectedId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use memoized selectors
  const tapping_points_raw = useSelector(selectTappingPoints);
  const tapping_points = useMemo(
    () =>
      Array.isArray(tapping_points_raw)
        ? tapping_points_raw
        : tapping_points_raw
          ? Object.values(tapping_points_raw)
          : [],
    [tapping_points_raw]
  );
  const background_musics = useSelector(selectBackgroundMusics);

  // Membership chips on the edit form are driven by the new APIv2 lists.
  const recommendedListItems = useSelector(state => state.recommended_list?.items);
  const popularListItems = useSelector(state => state.popular_list?.items);

  const isOnRecommendedList = useMemo(() => {
    if (!Array.isArray(recommendedListItems) || !selectedId) return false;
    const id = Number(selectedId);
    return recommendedListItems.some(s => Number(s.session_id) === id);
  }, [recommendedListItems, selectedId]);

  const isOnPopularList = useMemo(() => {
    if (!Array.isArray(popularListItems) || !selectedId) return false;
    const id = Number(selectedId);
    return popularListItems.some(s => Number(s.session_id) === id);
  }, [popularListItems, selectedId]);

  // State hooks
  const [formData, setFormData] = useState({
    session_name: "",
    session_allow_rating: 0,
    session_text_page_url: "",
    session_text_page_ios_url: "",
    session_description: "",
    session_main_url: "",
    session_main_url_preview: "",
    session_main_url_uploading: false,
    session_music_url: "",
    session_music_url_preview: "",
    session_music_url_uploading: false,
    session_image: "",
    session_image_preview: "",
    session_image_uploading: false,
    session_text_image_url: "",
    session_text_image_url_preview: "",
    session_text_image_url_uploading: false,
    session_grayscale_image: "",
    session_grayscale_image_preview: "",
    session_grayscale_image_uploading: false,
    session_is_active: 0,
    session_is_chapter: 0,
    session_skip_captions: 0,
    session_is_free: 1,
    session_media_type: 1,
    session_rating_start: 0,
    session_rating_end: 0,
    no_avatar: 0,
    session_is_recommended: 0,
    session_author_id: "",
    session_bg_timestamps: [{ tapping_point_id: "", timestamp: 0 }],
    associatedParent: "0",
    associatedSeries: "0",
    session_content_start_timestamp: 0,
    session_label: "",
    session_tags: [],
    tags: [],
    session_length: "",
    session_length_in_sec: 0,
    session_is_new: 0,
    session_is_dark_mode: 0,
    is_bgm_locked: 0,
    session_bgm_id: "0",
    session_is_duplicate: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const renderBGMOptions = useCallback(() => {
    if (!background_musics) return [];

    const options = background_musics.map(obj => ({
      label: obj.bgm_title,
      value: String(obj.bgm_id),
      prop: "session_bgm_id",
      session_music_url: obj.bgm_url,
    }));

    const none = {
      label: "none",
      value: "0",
      prop: "session_bgm_id",
      session_music_url: "",
    };

    return [none, ...options];
  }, [background_musics]);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Clear any existing data
        dispatch(clearMedia());
        dispatch(clearSessions());

        // Load all required data in parallel
        await Promise.all([
          dispatch(fetchAllCategories(0)),
          dispatch(fetchAllSubCategories(null, 0)),
          dispatch(fetchAuthors()),
          dispatch(fetchAllChallenges()),
          dispatch(fetchAllSeries()),
          dispatch(fetchTappingPoints()),
          dispatch(fetchAllBGM()),
        ]);

        // If in edit mode, load session data
        if (isEdit) {
          // Fire-and-forget — chip rendering tolerates undefined while loading.
          // Swallow errors so a 404 on the new endpoints (until APIv2 PR #522
          // is deployed) doesn't block the rest of the form from rendering.
          dispatch(fetchRecommendedList()).catch(() => {});
          dispatch(fetchPopularList()).catch(() => {});

          setIsLoadingSession(true);
          try {
            const response = await dispatch(fetchOneSession(selectedId));
            if (response?.data?.objects?.[0]) {
              const sessionData = response.data.objects[0];
              let associatedParent = "0";
              if (sessionData.category_id) {
                associatedParent = `${sessionData.category_id}CAT`;
              } else if (sessionData.subcategory_id) {
                associatedParent = `${sessionData.subcategory_id}SUB`;
              } else if (sessionData.challenge_id) {
                associatedParent = `${sessionData.challenge_id}CHA`;
              } else if (sessionData.series_id) {
                associatedParent = `${sessionData.series_id}SER`;
              }
              setFormData({
                ...sessionData,
                // APIv2 returns session_bgm_id as int (or null for "no music").
                // The BGM dropdown's option values are strings ("0" = none,
                // "1"..."N" = real bgms) and findOptionByValue does a strict
                // `option.value === String(selectedValue)` comparison. Without
                // this normalization, `null` becomes "null", no option matches,
                // and the dropdown renders empty for sessions saved without a
                // BGM. Coerce null/undefined to "0" so the "none" option is
                // preselected, and stringify ints so they match real options.
                session_bgm_id: sessionData.session_bgm_id != null ? String(sessionData.session_bgm_id) : "0",
                associatedParent,
              });
            } else {
              setError("Failed to load session data. Please try again.");
            }
          } catch (err) {
            setError("Failed to load session data. Please try again.");
          } finally {
            setIsLoadingSession(false);
          }
        }

        setIsLoading(false);
      } catch (err) {
        setError("Failed to load required data. Please try again.");
        setIsLoading(false);
      }
    };

    loadData();
  }, [dispatch, isEdit, selectedId]);

  // Separate effect to handle BGM data
  useEffect(() => {
    if (background_musics?.length > 0) {
      // BGM data is loaded and ready
    }
  }, [background_musics, formData.session_bgm_id]);

  // Media upload side effects (preview URLs, Redux media processing, cleanup)
  useSessionFormMedia(formData, setFormData);

  // Ref to access TappingPointsTimestamp methods
  const timestampComponentRef = useRef(null);

  // Show loading state
  if (isLoading || isLoadingSession) {
    return <LoadingBackdrop open={true} />;
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button component={Link} to="/sessions" variant="contained" sx={{ mt: 2 }}>
          Return to Sessions
        </Button>
      </Box>
    );
  }

  // Event Handlers
  const handleFieldChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = e => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked ? 1 : 0,
    }));
  };

  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke existing preview URL to prevent memory leak
    if (formData.session_image_preview) {
      URL.revokeObjectURL(formData.session_image_preview);
    }

    setFormData(prev => ({
      ...prev,
      session_image_uploading: true,
      session_image_preview: URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file));
  };

  const handleMainMediaUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke existing preview URL to prevent memory leak
    if (formData.session_main_url_preview) {
      URL.revokeObjectURL(formData.session_main_url_preview);
    }

    const previewUrl = URL.createObjectURL(file);

    try {
      setFormData(prev => ({
        ...prev,
        session_main_url_uploading: true,
        session_main_url_preview: previewUrl,
      }));

      const response = await dispatch(uploadAudio(file));

      // Check if the upload failed - the response should have data with audio_file_url
      if (!response?.data?.audio_file_url) {
        throw new Error("Upload failed - no audio URL received");
      }
    } catch (err) {
      console.error("Error uploading audio:", err);

      // Clean up the preview URL
      URL.revokeObjectURL(previewUrl);

      // Reset loading state on error
      setFormData(prev => ({
        ...prev,
        session_main_url_uploading: false,
        session_main_url_preview: "",
      }));

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Failed to upload audio file. Please try again.",
      });
    }
  };

  const handleAudioLoadedData = e => {
    const duration = e.target.duration;
    if (duration && !isNaN(duration)) {
      setFormData(prev => ({
        ...prev,
        session_length_in_sec: Math.floor(duration),
        session_length: formatTimeToMMSS(duration),
      }));
    }
  };

  const handleDeepLinkImageUpload = e => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke existing preview URL to prevent memory leak
    if (formData.session_text_image_url_preview) {
      URL.revokeObjectURL(formData.session_text_image_url_preview);
    }

    setFormData(prev => ({
      ...prev,
      session_text_image_url_uploading: true,
      session_text_image_url_preview: URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file));
  };

  const handleTimestampChange = (value, stateProp, index, field) => {
    setFormData(prev => ({
      ...prev,
      session_bg_timestamps: prev.session_bg_timestamps.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addTimestamp = () => {
    const newIndex = formData.session_bg_timestamps.length;
    setFormData(prev => {
      const newTimestamps = [...prev.session_bg_timestamps, { tapping_point_id: "", timestamp: 0 }];
      return {
        ...prev,
        session_bg_timestamps: newTimestamps,
      };
    });

    // Focus on the new timestamp field using the component ref
    if (timestampComponentRef.current) {
      timestampComponentRef.current.focusOnNewItem(newIndex);
    }
  };

  const removeTimestamp = index => {
    setFormData(prev => ({
      ...prev,
      session_bg_timestamps: prev.session_bg_timestamps.filter((_, i) => i !== index),
    }));
  };

  const handleImportTimestamps = importedTimestamps => {
    setFormData(prev => ({
      ...prev,
      session_bg_timestamps: [
        ...prev.session_bg_timestamps,
        ...importedTimestamps.map(ts => ({
          tapping_point_id: ts.tapping_point_id,
          timestamp: ts.timestamp,
          image_url: ts.image_url,
        })),
      ],
    }));
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the session. You can't undo this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      dispatch(deleteSession(selectedId, () => navigate("/sessions")));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Extract parent IDs from associatedParent
      const parentValue = formData.associatedParent;
      let category_id = 0;
      let subcategory_id = 0;
      let series_id = 0;
      let challenge_id = 0;

      if (parentValue !== "0") {
        const [id, type] = parentValue.split(/(?<=[0-9])(?=[A-Z])/);
        switch (type) {
          case "CAT":
            category_id = parseInt(id);
            break;
          case "SUB":
            subcategory_id = parseInt(id);
            break;
          case "SER":
            series_id = parseInt(id);
            break;
          case "CHA":
            challenge_id = parseInt(id);
            break;
        }
      }

      // Prepare the data for submission
      const sessionData = {
        ...formData,
        category_id,
        subcategory_id,
        series_id,
        challenge_id,
      };

      // Remove fields that shouldn't be in the session_json
      delete sessionData.associatedParent;
      delete sessionData.tags;
      delete sessionData.session_image_preview;
      delete sessionData.session_main_url_preview;
      delete sessionData.session_text_image_url_preview;
      delete sessionData.session_image_uploading;
      delete sessionData.session_main_url_uploading;
      delete sessionData.session_text_image_url_uploading;

      const response = await dispatch(
        addEditSession({
          timestamps_json: JSON.stringify(formData.session_bg_timestamps),
          session_json: JSON.stringify(sessionData),
          category_id,
          subcategory_id,
          series_id,
          challenge_id,
        })
      );

      if (response?.payload) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: isEdit ? "Session updated successfully!" : "Session created successfully!",
          timer: 1000,
        }).then(() => {
          navigate("/sessions");
        });
      }
    } catch (err) {
      console.error("Error saving session:", err);
      const apiMessage = err?.response?.data?.message;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiMessage || "Failed to save session. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <Box
        component="form"
        id="session-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
      >
        <Box display="grid" gap={4}>
          {/* Basic Information Section */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="h6">Basic Information</Typography>
              {formData.session_is_duplicate && <Chip label="Duplicated Session" size="small" color="warning" />}
            </Box>
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
              <TextField
                fullWidth
                label="Session Name"
                name="session_name"
                value={formData.session_name}
                onChange={handleFieldChange}
                required
              />
              <ParentSelector
                value={formData.associatedParent}
                onChange={value => {
                  setFormData(prev => ({
                    ...prev,
                    associatedParent: value,
                  }));
                }}
                allowedTypes={["category", "subcategory", "challenge", "series", "audiobook"]}
                showQuickTaps={false}
                label="Parent"
              />
              <AuthorSelector
                value={formData.session_author_id}
                onChange={value => {
                  setFormData(prev => ({
                    ...prev,
                    session_author_id: value,
                  }));
                }}
                required
              />
            </Box>
          </Box>

          {/* Description Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
              gap={3}
              sx={{ alignItems: "flex-start" }}
            >
              <TextField
                fullWidth
                label="Description"
                name="session_description"
                value={formData.session_description}
                onChange={handleFieldChange}
                multiline
                rows={2}
              />
              <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {/* Group 1 */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="session_is_active"
                          checked={formData.session_is_active}
                          onChange={e =>
                            setFormData(prev => ({ ...prev, session_is_active: e.target.checked ? 1 : 0 }))
                          }
                        />
                      }
                      label="Active"
                      sx={{ minWidth: 160 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.session_allow_rating}
                          onChange={handleCheckBox}
                          name="session_allow_rating"
                        />
                      }
                      label="Show Rating"
                      sx={{ minWidth: 160 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch checked={formData.session_is_new} onChange={handleCheckBox} name="session_is_new" />
                      }
                      label="Is New"
                      sx={{ minWidth: 160 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="session_is_free"
                          checked={formData.session_is_free}
                          onChange={e => setFormData(prev => ({ ...prev, session_is_free: e.target.checked ? 1 : 0 }))}
                        />
                      }
                      label="Free Content"
                      sx={{ minWidth: 160 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* Tags Section */}
          {isEdit && (
            <TagsSection
              tags={formData.tags}
              onTagsChange={newTags => setFormData(prev => ({ ...prev, tags: newTags }))}
              objectId={formData.session_id}
              objectType="Session"
            />
          )}
          {/* Session Settings Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Session Settings
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.session_is_dark_mode}
                    onChange={handleCheckBox}
                    name="session_is_dark_mode"
                  />
                }
                label="Dark Mode"
                sx={{ minWidth: 160 }}
              />
              <FormControlLabel
                control={<Switch checked={formData.no_avatar} onChange={handleCheckBox} name="no_avatar" />}
                label="No Avatar"
                sx={{ minWidth: 160 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.session_is_recommended}
                    onChange={handleCheckBox}
                    name="session_is_recommended"
                  />
                }
                label="Is Recommended"
                sx={{ minWidth: 160 }}
              />
              {isEdit && (isOnRecommendedList || isOnPopularList) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    minWidth: 160,
                  }}
                >
                  {isOnRecommendedList && (
                    <Tooltip title="Managed via Engagement & Promotions → Default Recommended List">
                      <Chip label="Default Recommended" color="primary" size="small" variant="outlined" />
                    </Tooltip>
                  )}
                  {isOnPopularList && (
                    <Tooltip title="Managed via Engagement & Promotions → Popular List">
                      <Chip label="Popular" color="secondary" size="small" variant="outlined" />
                    </Tooltip>
                  )}
                </Box>
              )}
              <FormControlLabel
                control={
                  <Switch
                    name="session_is_chapter"
                    checked={formData.session_is_chapter}
                    onChange={e => setFormData(prev => ({ ...prev, session_is_chapter: e.target.checked ? 1 : 0 }))}
                  />
                }
                label="Is Chapter"
                sx={{ minWidth: 160 }}
              />
              <Tooltip
                title={
                  "Use for background-music-only or silence-by-design audio. " +
                  "When ON, the captions pipeline skips this session entirely " +
                  "and any existing caption rows + VTT files are deleted. " +
                  "Turning it OFF later does NOT regenerate captions automatically — " +
                  "you'd need to click Regenerate from the captions editor."
                }
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="session_skip_captions"
                      checked={!!formData.session_skip_captions}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, session_skip_captions: e.target.checked ? 1 : 0 }))
                      }
                    />
                  }
                  label="Skip caption generation"
                  sx={{ minWidth: 220 }}
                />
              </Tooltip>
            </Box>
          </Box>

          {/* Sharing Settings & Media Section (Combined) */}
          <SessionMediaSection
            formData={formData}
            setFormData={setFormData}
            onImageUpload={handleImageUpload}
            onDeepLinkImageUpload={handleDeepLinkImageUpload}
            onMainMediaUpload={handleMainMediaUpload}
            onAudioLoadedData={handleAudioLoadedData}
          />
          {/* Background Music & Content Markers Section */}
          <Box>
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={4} alignItems="start">
              {/* Left: Background Music Selector */}
              <Box sx={{ height: "100%" }}>
                <BackgroundMusicSelector
                  title="Background Music"
                  options={renderBGMOptions()}
                  selectedValue={formData.session_bgm_id}
                  isLocked={formData.is_bgm_locked}
                  audioUrl={formData.session_music_url}
                  isLoading={isLoading}
                  onSelectionChange={newValue => {
                    setFormData(prev => ({
                      ...prev,
                      session_bgm_id: newValue ? newValue.value : "0",
                      session_music_url: newValue ? newValue.session_music_url : "",
                    }));
                  }}
                  onLockChange={checked => {
                    setFormData(prev => ({
                      ...prev,
                      is_bgm_locked: checked ? 1 : 0,
                    }));
                  }}
                  onRemove={() => {
                    setFormData(prev => ({
                      ...prev,
                      session_bgm_id: "0",
                      session_music_url: "",
                    }));
                  }}
                  showCopyUrlButton={true}
                  sx={{ height: "100%", width: "100%" }}
                />
              </Box>
              {/* Right: Content Markers */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Content Markers
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Box display="grid" gap={1}>
                    <TimeInput
                      dense
                      label="Content Start Time"
                      value={formatTimeToMMSS(formData.session_content_start_timestamp)}
                      onChange={value =>
                        setFormData(prev => ({ ...prev, session_content_start_timestamp: parseMMSSToSeconds(value) }))
                      }
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: formData.session_allow_rating ? "text.primary" : "text.disabled",
                        }}
                      >
                        Rating Timestamps
                      </Typography>
                      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                        <TimeInput
                          dense
                          label="Start Time"
                          value={formatTimeToMMSS(formData.session_rating_start)}
                          onChange={value =>
                            setFormData(prev => ({ ...prev, session_rating_start: parseMMSSToSeconds(value) }))
                          }
                          disabled={!formData.session_allow_rating}
                        />
                        <TimeInput
                          dense
                          label="End Time"
                          value={formatTimeToMMSS(formData.session_rating_end)}
                          onChange={value =>
                            setFormData(prev => ({ ...prev, session_rating_end: parseMMSSToSeconds(value) }))
                          }
                          disabled={!formData.session_allow_rating}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
          {/* Timestamps Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Tapping Point Timings
            </Typography>
            <Paper sx={{ p: 2 }}>
              <TappingPointsTimestamp
                ref={timestampComponentRef}
                dense
                items={formData.session_bg_timestamps}
                removeTimestamp={removeTimestamp}
                stateProp="session_bg_timestamps"
                handleTimestampChange={handleTimestampChange}
                tappingPoints={tapping_points}
                onImportTimestamps={handleImportTimestamps}
                maxDurationSeconds={null} // TODO: Could be calculated from audio/video duration if available
              />
              <Button variant="contained" onClick={addTimestamp} sx={{ mt: 2 }} data-add-timestamp-button>
                Add Timestamp
              </Button>
            </Paper>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            {isEdit && (
              <Button variant="contained" color="error" onClick={handleDelete}>
                Delete Session
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AddEditSession;
